import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Sun, Moon, Eye, Save, RotateCcw, Download, ShieldCheck, ShieldAlert, Building2, Undo2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { GradientEditor } from "./GradientEditor";
import { exportTheme } from "./ImportExport";
import { ConfirmDialog } from "@/components/dialogs";
import {
  SWATCH_GROUPS, FONT_CHOICES, DENSITIES, SHADOWS, RADII,
  tripletToHex, hexToTriplet, evaluateContrast, diffTokens,
} from "@/lib/appearance";

const LEVEL_STYLE = {
  AAA: "bg-emerald-100 text-emerald-800 border-emerald-300",
  AA: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "AA Large": "bg-amber-50 text-amber-700 border-amber-200",
  Fail: "bg-red-50 text-red-700 border-red-300",
};

function ColourGrid({ tokens, baseTokens, changed, onChange, onReset }) {
  return (
    <div className="space-y-4">
      {SWATCH_GROUPS.map((group) => (
        <div key={group.label}>
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{group.label}</div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {group.swatches.map((s) => {
              const hex = tripletToHex(tokens?.[s.key]);
              const isMod = changed.has(s.key);
              return (
                <div key={s.key} className={cn("relative flex items-center gap-2 rounded-lg border p-2", isMod ? "border-accent" : "border-border")}>
                  <input type="color" value={hex} onChange={(e) => onChange(s.key, hexToTriplet(e.target.value))}
                    className="h-8 w-8 shrink-0 cursor-pointer rounded border-0 bg-transparent p-0"
                    data-testid={`swatch-${s.key}`} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-1 truncate text-xs font-medium text-foreground">
                      {s.label}
                      {isMod && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" title="Changed from organisation default" />}
                    </div>
                    <div className="font-mono text-[10px] uppercase text-muted-foreground">{hex}</div>
                  </div>
                  {isMod && baseTokens?.[s.key] && (
                    <button onClick={() => onReset(s.key)} title="Reset to organisation default"
                      className="ml-auto text-muted-foreground hover:text-foreground" data-testid={`swatch-reset-${s.key}`}>
                      <Undo2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function ContrastTab({ tokens }) {
  const results = useMemo(() => evaluateContrast(tokens), [tokens]);
  const fails = results.filter((r) => r.level === "Fail").length;
  return (
    <div className="space-y-3" data-testid="contrast-tab">
      <div className={cn("flex items-center gap-2 rounded-lg border p-3 text-sm",
        fails ? "border-red-300 bg-red-50 text-red-800" : "border-emerald-300 bg-emerald-50 text-emerald-800")}>
        {fails ? <ShieldAlert className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
        {fails ? `${fails} pair${fails > 1 ? "s" : ""} fail WCAG AA — adjust the colours below.` : "All key pairs meet WCAG AA. "}
      </div>
      <div className="space-y-1.5">
        {results.map((r) => (
          <div key={r.label} className="flex items-center justify-between rounded-md border border-border px-3 py-2" data-testid={`contrast-${r.fg}-${r.bg}`}>
            <div className="flex items-center gap-2">
              <span className="flex h-6 items-center rounded px-2 text-[11px] font-medium"
                style={{ background: `rgb(${tokens?.[r.bg]})`, color: `rgb(${tokens?.[r.fg]})`, border: "1px solid rgb(var(--border))" }}>Aa</span>
              <span className="text-sm">{r.label}{r.large && <span className="ml-1 text-[10px] text-muted-foreground">(large)</span>}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-muted-foreground">{r.ratio.toFixed(2)}:1</span>
              <span className={cn("rounded-full border px-2 py-0.5 text-[10px] font-semibold", LEVEL_STYLE[r.level])} data-testid={`aa-badge-${r.fg}`}>{r.level}</span>
            </div>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground">AA needs 4.5:1 for normal text and 3:1 for large text; AAA needs 7:1. Badges update live as you edit colours.</p>
    </div>
  );
}

export function ThemeEditor({ open, onOpenChange, theme, baseConfig, baseName, onSave, onPreview }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [config, setConfig] = useState(null);
  const [colourMode, setColourMode] = useState("light");
  const [confirm, setConfirm] = useState(null); // {title, description, onConfirm}

  useEffect(() => {
    if (theme) {
      setName(theme.name);
      setDescription(theme.description || "");
      setConfig(JSON.parse(JSON.stringify(theme.config || {})));
    }
  }, [theme]);

  const base = baseConfig || theme?.config;
  const changed = useMemo(() => diffTokens(config || {}, base || {}, colourMode), [config, base, colourMode]);
  const totalChanged = useMemo(() => (config && base
    ? diffTokens(config, base, "light").size + diffTokens(config, base, "dark").size : 0), [config, base]);

  if (!config) return null;

  const setToken = (mode, key, value) => setConfig((c) => ({ ...c, [mode]: { ...(c[mode] || {}), [key]: value } }));
  const setTypo = (key, value) => setConfig((c) => ({ ...c, typography: { ...(c.typography || {}), [key]: value } }));
  const setField = (key, value) => setConfig((c) => ({ ...c, [key]: value }));
  const resetKey = (key) => { if (base?.[colourMode]?.[key]) setToken(colourMode, key, base[colourMode][key]); };
  const revertAllToOrg = () => setConfirm({
    title: "Revert all colours to the organisation default?",
    description: `This replaces every colour in “${name}” with the organisation default${baseName ? ` (${baseName})` : ""}. Typography, layout and gradients are untouched. You still need to Save.`,
    confirmLabel: "Revert colours",
    onConfirm: () => { setConfig((c) => ({ ...c, light: { ...(base.light || {}) }, dark: { ...(base.dark || {}) } })); setConfirm(null); },
  });
  const revertEdits = () => setConfirm({
    title: "Discard unsaved changes?",
    description: "This returns the editor to the last saved version of this theme.",
    confirmLabel: "Discard changes",
    onConfirm: () => { setConfig(JSON.parse(JSON.stringify(theme.config))); setConfirm(null); },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto scrollbar-thin" data-testid="theme-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Edit theme</DialogTitle>
          <DialogDescription>Customise colours, typography, layout and gradients. Changes preview live.</DialogDescription>
        </DialogHeader>

        {base && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs" data-testid="override-banner">
            <Building2 className="h-3.5 w-3.5 text-accent" />
            <span className="text-muted-foreground">Based on organisation default {baseName && <b className="text-foreground">{baseName}</b>}.</span>
            <span className={cn("ml-auto rounded-full px-2 py-0.5 font-medium", totalChanged ? "bg-accent-subtle text-foreground" : "bg-secondary text-secondary-foreground")}
              data-testid="override-count">{totalChanged ? `${totalChanged} colour override${totalChanged > 1 ? "s" : ""}` : "No overrides — matches org default"}</span>
          </div>
        )}

        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" data-testid="theme-name-input" />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1" data-testid="theme-desc-input" />
          </div>
        </div>

        <Tabs defaultValue="colours">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="colours" data-testid="tab-colours">Colours</TabsTrigger>
            <TabsTrigger value="contrast" data-testid="tab-contrast">Contrast</TabsTrigger>
            <TabsTrigger value="type" data-testid="tab-type">Typography</TabsTrigger>
            <TabsTrigger value="layout" data-testid="tab-layout">Layout</TabsTrigger>
            <TabsTrigger value="gradients" data-testid="tab-gradients">Gradients</TabsTrigger>
          </TabsList>

          <div className="mt-4 flex rounded-lg border border-border p-0.5" data-testid="colour-mode-switch">
            {[["light", Sun, "Light"], ["dark", Moon, "Dark"]].map(([v, Icon, label]) => (
              <button key={v} onClick={() => setColourMode(v)} data-testid={`colour-mode-${v}`}
                className={cn("flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                  colourMode === v ? "bg-secondary text-foreground" : "text-muted-foreground")}>
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>

          <TabsContent value="colours" className="mt-3">
            <ColourGrid tokens={config[colourMode]} baseTokens={base?.[colourMode]} changed={changed}
              onChange={(k, v) => setToken(colourMode, k, v)} onReset={resetKey} />
            {base && totalChanged > 0 && (
              <Button variant="ghost" size="sm" className="mt-3 gap-1.5 text-muted-foreground" onClick={revertAllToOrg} data-testid="revert-all-org">
                <Undo2 className="h-3.5 w-3.5" /> Revert all colours to organisation default
              </Button>
            )}
          </TabsContent>

          <TabsContent value="contrast" className="mt-3">
            <ContrastTab tokens={config[colourMode]} />
          </TabsContent>

          <TabsContent value="type" className="mt-4 space-y-4">
            {["heading", "body", "mono"].map((slot) => (
              <div key={slot}>
                <Label className="text-xs capitalize">{slot} font</Label>
                <Select value={config.typography?.[slot]} onValueChange={(v) => setTypo(slot, v)}>
                  <SelectTrigger className="mt-1" data-testid={`font-${slot}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{FONT_CHOICES[slot].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ))}
            <div>
              <Label className="text-xs">Base scale · {Math.round((config.typography?.scale || 1) * 100)}%</Label>
              <Slider min={90} max={115} step={1} value={[(config.typography?.scale || 1) * 100]}
                onValueChange={([v]) => setTypo("scale", v / 100)} className="mt-2" data-testid="font-scale" />
            </div>
          </TabsContent>

          <TabsContent value="layout" className="mt-4 space-y-4">
            <div>
              <Label className="text-xs">Corner radius</Label>
              <div className="mt-1 flex flex-wrap gap-2">
                {RADII.map((r) => (
                  <button key={r.value} onClick={() => setField("radius", r.value)} data-testid={`radius-${r.value}`}
                    className={cn("rounded-md border px-3 py-1.5 text-xs", config.radius === r.value ? "border-accent bg-accent-subtle" : "border-border")}>{r.label}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Shadows</Label>
              <div className="mt-1 flex flex-wrap gap-2">
                {SHADOWS.map((s) => (
                  <button key={s} onClick={() => setField("shadows", s)} data-testid={`shadow-${s}`}
                    className={cn("rounded-md border px-3 py-1.5 text-xs capitalize", config.shadows === s ? "border-accent bg-accent-subtle" : "border-border")}>{s}</button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">Density</Label>
              <div className="mt-1 flex flex-wrap gap-2">
                {DENSITIES.map((d) => (
                  <button key={d} onClick={() => setField("density", d)} data-testid={`density-${d}`}
                    className={cn("rounded-md border px-3 py-1.5 text-xs capitalize", config.density === d ? "border-accent bg-accent-subtle" : "border-border")}>{d}</button>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="gradients" className="mt-4">
            <GradientEditor gradients={config.gradients} onChange={(g) => setField("gradients", g)} />
          </TabsContent>
        </Tabs>

        <div className="mt-5 flex flex-wrap items-center justify-end gap-2 border-t border-border pt-4">
          <Button variant="ghost" size="sm" className="mr-auto gap-1.5" onClick={() => exportTheme(theme.id, name)} data-testid="editor-export">
            <Download className="h-4 w-4" /> Export
          </Button>
          <Button variant="ghost" className="gap-1.5" onClick={revertEdits} data-testid="editor-reset">
            <RotateCcw className="h-4 w-4" /> Discard changes
          </Button>
          <Button variant="outline" className="gap-1.5" onClick={() => onPreview({ ...config, name })} data-testid="editor-preview">
            <Eye className="h-4 w-4" /> Preview
          </Button>
          <Button className="gap-1.5" onClick={() => onSave({ id: theme.id, name, description, config })} data-testid="editor-save">
            <Save className="h-4 w-4" /> Save theme
          </Button>
        </div>
      </DialogContent>

      {confirm && (
        <ConfirmDialog open onOpenChange={(o) => !o && setConfirm(null)}
          title={confirm.title} description={confirm.description}
          confirmLabel={confirm.confirmLabel} variant="destructive" onConfirm={confirm.onConfirm} />
      )}
    </Dialog>
  );
}
